import { SidebarMenu } from '../_models/menu/SidebarMenu.model';

// Organizzazione menu per azienda (cmp_menu_org, JSON su cmp_companies).
// La riorganizzazione è puramente presentazionale: viene applicata al menu già
// risolto dai preset lato server, quindi l'insieme delle voci (e degli URL visti
// da PageAccessGuard & co.) resta sempre esattamente quello assegnato all'azienda.
// Un'azienda può avere più menu salvati (profili, es. uno di prova): ne è attivo
// al massimo uno (activeId); nessun profilo attivo = menu standard.

export interface MenuOrgCategory {
	// Id stabile: le categorie seedate dal menu globale usano String(pg_id) così lo
	// stato collapse in localStorage (`submenu_<id>`) sopravvive alla personalizzazione;
	// le categorie nuove usano un id generato ('c...'). Non cambia mai dopo la creazione.
	id: string;
	// Testo libero oppure chiave i18n: il pipe translate fa fallback al testo raw.
	label: string;
	icon?: string;
	// pg_id ordinati delle voci, oppure nodi annidati (sottocategorie).
	// Il numero nudo (formato storico) equivale a { type: 'item', pg_id }.
	items: MenuOrgChild[];
}

// 'shortcut': collegamento rapido a una sezione azienda (catalogo COMPANY_MENU_SHORTCUTS);
// opt-in (non fa parte del preset) e visibile solo se il ruolo dell'utente ha la
// funzione modulo corrispondente — la risoluzione avviene in getMenuItems.
// 'item' e 'shortcut' possono avere `items`: voce navigabile CON sottomenu (click
// naviga, hover/expand mostra i figli — es. gli hub con le card e i loro sotto-hub).
// 'item' puo' avere `label`: nome della voce in questo profilo al posto del titolo della pagina (prt_page.pg_title).
export type MenuOrgNode = { type: 'item'; pg_id: number; label?: string; items?: MenuOrgChild[] } | { type: 'category'; cat: MenuOrgCategory } | { type: 'shortcut'; key: string; items?: MenuOrgChild[] };

export type MenuOrgChild = number | MenuOrgNode;

/** Normalizza un figlio di categoria (numero nudo o nodo) a MenuOrgNode; null se malformato. */
export function asMenuOrgNode(child: MenuOrgChild): MenuOrgNode | null {
	if (typeof child === 'number') {
		return { type: 'item', pg_id: child };
	}
	if (child && child.type === 'item' && typeof child.pg_id === 'number') {
		return child;
	}
	if (child && child.type === 'category' && child.cat) {
		return child;
	}
	if (child && child.type === 'shortcut' && typeof child.key === 'string' && child.key) {
		return child;
	}
	return null;
}

/** Chiavi delle scorciatoie presenti tra i nodi (anche annidate), senza duplicati. */
export function collectMenuOrgShortcutKeys(nodes: MenuOrgNode[] | null | undefined): string[] {
	const keys = new Set<string>();
	const visit = (children: MenuOrgChild[]) => {
		for (const child of children || []) {
			const node = asMenuOrgNode(child);
			if (!node) continue;
			if (node.type === 'shortcut') {
				keys.add(node.key);
				visit(node.items || []);
			} else if (node.type === 'category') {
				visit(node.cat.items || []);
			} else if (node.items?.length) {
				visit(node.items);
			}
		}
	};
	visit(nodes || []);
	return Array.from(keys);
}

export interface MenuOrgProfile {
	id: string;
	name: string;
	nodes: MenuOrgNode[];
}

export interface CompanyMenuOrgStore {
	version: 2;
	/** Profilo attivo nel portale; null = menu standard */
	activeId: string | null;
	profiles: MenuOrgProfile[];
	/** Sezioni mostrate come cruscotto + schede al posto del sottomenu (chiavi di MENU_HUBS, vedi menu-hub.ts) */
	hubs?: string[];
}

/** True se la voce è una categoria/cartella (nessuna pagina associata). */
export function isMenuCategory(item: SidebarMenu): boolean {
	const url = (item.url || '').trim();
	return url === '' || url === 'null' || url === '#';
}

/** Appiattisce l'albero alle sole voci navigabili (url valorizzato), in ordine di visita. */
export function flattenMenuLeaves(menu: SidebarMenu[]): SidebarMenu[] {
	const leaves: SidebarMenu[] = [];
	const visit = (items: SidebarMenu[]) => {
		for (const item of items || []) {
			if (!isMenuCategory(item)) {
				leaves.push(item);
			}
			if (item.submenu?.length) {
				visit(item.submenu);
			}
		}
	};
	visit(menu);
	return leaves;
}

export function newMenuOrgId(prefix: string = 'c'): string {
	return prefix + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

// ---- collocazione delle voci non assegnate dal profilo ----
// Una pagina aggiunta al preset DOPO che l'azienda ha personalizzato il menu non
// compare nei nodi del profilo. Invece di appenderla in coda al top-level, la si
// colloca dove starebbe nel menu standard: dentro la categoria corrispondente (se
// presente nel profilo) e nell'ordine standard rispetto ai fratelli già presenti.

/** Accessori per applicare la collocazione a un albero qualsiasi (menu del portale o VM dell'editor). */
export interface MenuTreeAccess<T> {
	children: (node: T) => T[] | undefined;
	/** pg_id della pagina rappresentata dal nodo; undefined per categorie e scorciatoie. */
	pageId: (node: T) => number | undefined;
	/** True se il nodo è la categoria del profilo che corrisponde alla categoria `raw` del menu standard. */
	isCategoryOf: (node: T, raw: SidebarMenu) => boolean;
}

/** Antenati (dal più vicino al più lontano) della pagina `pg_id` nel menu standard. */
function rawMenuAncestors(menu: SidebarMenu[], pg_id: number): SidebarMenu[] {
	const find = (items: SidebarMenu[], stack: SidebarMenu[]): SidebarMenu[] | null => {
		for (const item of items || []) {
			if (!isMenuCategory(item) && item.pg_id === pg_id) {
				return stack;
			}
			if (item.submenu?.length) {
				const found = find(item.submenu, [item, ...stack]);
				if (found) {
					return found;
				}
			}
		}
		return null;
	};
	return find(menu, []) || [];
}

/** Prima occorrenza nell'albero (ricorsiva) del contenitore che corrisponde all'antenato standard `raw`. */
function findContainer<T>(root: T[], raw: SidebarMenu, access: MenuTreeAccess<T>): T | null {
	for (const node of root || []) {
		const kids = access.children(node);
		if (isMenuCategory(raw)) {
			if (access.isCategoryOf(node, raw)) {
				return node;
			}
		} else if (access.pageId(node) === raw.pg_id && kids?.length) {
			// pagina con sottomenu: è un contenitore solo se nel profilo ha già dei figli
			// (altrimenti una voce semplice diventerebbe all'improvviso un menu a tendina)
			return node;
		}
		if (kids?.length) {
			const found = findContainer(kids, raw, access);
			if (found) {
				return found;
			}
		}
	}
	return null;
}

/**
 * Inserisce la foglia `leaf` (non assegnata dal profilo) nell'albero `root`, nel punto
 * che occuperebbe nel menu standard `rawMenu`: dentro il contenitore standard più vicino
 * presente nel profilo (categoria omologa per id o etichetta; pagina con sottomenu già
 * popolato), dopo l'ultimo fratello standard che la precede — altrimenti prima del primo
 * che la segue, altrimenti in fondo al contenitore. Se nessun antenato è presente, in
 * coda al top-level (comportamento storico).
 */
export function placeUnassignedLeaf<T>(root: T[], leaf: SidebarMenu, rawMenu: SidebarMenu[], node: T, access: MenuTreeAccess<T>): void {
	for (const ancestor of rawMenuAncestors(rawMenu, leaf.pg_id)) {
		const container = findContainer(root, ancestor, access);
		const list = container ? access.children(container) : undefined;
		if (!list) {
			continue;
		}
		// ordine standard di tutte le pagine discendenti dell'antenato
		const order = flattenMenuLeaves(ancestor.submenu || []).map((l) => l.pg_id);
		const pos = order.indexOf(leaf.pg_id);
		const before = new Set(order.slice(0, pos));
		const after = new Set(order.slice(pos + 1));
		let index = -1;
		for (let i = list.length - 1; i >= 0; i--) {
			const id = access.pageId(list[i]);
			if (id !== undefined && before.has(id)) {
				index = i + 1;
				break;
			}
		}
		if (index < 0) {
			index = list.findIndex((n) => {
				const id = access.pageId(n);
				return id !== undefined && after.has(id);
			});
		}
		list.splice(index < 0 ? list.length : index, 0, node);
		return;
	}
	root.push(node);
}

/** Accessori per il menu del portale prodotto da applyMenuOrganization. */
export const SIDEBAR_MENU_ACCESS: MenuTreeAccess<SidebarMenu> = {
	children: (n) => n.submenu,
	// scorciatoie e categorie virtuali hanno pg_id stringa: non sono pagine
	pageId: (n) => (!isMenuCategory(n) && typeof n.pg_id === 'number' ? n.pg_id : undefined),
	isCategoryOf: (n, raw) => isMenuCategory(n) && (String(n.pg_id) === String(raw.pg_id) || n.pg_title === raw.pg_title),
};

function virtualCategory(cat: MenuOrgCategory, index: number, children: SidebarMenu[]): SidebarMenu {
	return {
		id: cat.id as unknown as number,
		pg_id: cat.id as unknown as number, // stringa: usata solo come chiave (localStorage, tracking)
		name: cat.label,
		pg_title: cat.label,
		url: '',
		styleclass: cat.icon || 'pi pi-folder',
		pg_posizione: index,
		submenu: children,
	};
}

/**
 * Riorganizza il menu risolto dai preset secondo i nodi del profilo attivo.
 * Invarianti: l'insieme delle foglie di PAGINA in output = esattamente quello in input
 * (nodi di pagine non più assegnate → ignorati; pagine nuove non in config →
 * collocate nella categoria standard se presente nel profilo, altrimenti appese in
 * fondo al top-level, vedi placeUnassignedLeaf; categorie rimaste vuote → nascoste).
 * Le categorie possono contenere sottocategorie (ricorsione su cat.items).
 * `shortcutLeaves` (key → voce sintetica, già filtrata per ruolo) risolve i nodi
 * 'shortcut': chiavi assenti dalla mappa (ruolo senza accesso / catalogo cambiato)
 * vengono semplicemente saltate.
 */
export function applyMenuOrganization(menu: SidebarMenu[], nodes: MenuOrgNode[] | null | undefined, shortcutLeaves?: Map<string, SidebarMenu> | null): SidebarMenu[] {
	if (!nodes?.length) {
		return menu;
	}
	const leaves = flattenMenuLeaves(menu);
	const byId = new Map<number, SidebarMenu>(leaves.map((l) => [l.pg_id, l]));
	const consumed = new Set<number>();
	const consumedShortcuts = new Set<string>();

	const takeLeaf = (pg_id: number): SidebarMenu | null => {
		const leaf = byId.get(pg_id);
		if (!leaf || consumed.has(pg_id)) {
			return null;
		}
		consumed.add(pg_id);
		// submenu azzerato: gli eventuali figli della foglia nel menu raw sono
		// posizionati per conto proprio (evita voci duplicate nei sottomenu)
		return { ...leaf, submenu: [] };
	};

	const takeShortcut = (key: string): SidebarMenu | null => {
		const leaf = shortcutLeaves?.get(key);
		if (!leaf || consumedShortcuts.has(key)) {
			return null;
		}
		consumedShortcuts.add(key);
		return { ...leaf, submenu: [] };
	};

	const buildNodes = (orgNodes: MenuOrgChild[]): SidebarMenu[] => {
		const out: SidebarMenu[] = [];
		for (const child of orgNodes || []) {
			const node = asMenuOrgNode(child);
			if (!node) {
				continue;
			}
			if (node.type === 'item') {
				const leaf = takeLeaf(node.pg_id);
				if (leaf) {
					if (node.label) {
						leaf.pg_title = node.label;
					}
					// pagina con sottomenu: click naviga, hover/expand mostra i figli
					if (node.items?.length) {
						leaf.submenu = buildNodes(node.items);
					}
					out.push(leaf);
				}
			} else if (node.type === 'shortcut') {
				const leaf = takeShortcut(node.key);
				if (leaf) {
					// scorciatoia con sottomenu (sotto-hub); se il ruolo non vede il padre,
					// i figli spariscono con lui (le eventuali pagine risalgono al top-level)
					if (node.items?.length) {
						leaf.submenu = buildNodes(node.items);
					}
					out.push(leaf);
				}
			} else {
				const children = buildNodes(node.cat.items || []);
				if (children.length) {
					out.push(virtualCategory(node.cat, out.length, children));
				}
			}
		}
		return out;
	};

	const result = buildNodes(nodes);
	for (const leaf of leaves) {
		if (!consumed.has(leaf.pg_id)) {
			// pagina nuova non in config: nella categoria standard se presente, altrimenti in coda
			placeUnassignedLeaf(result, leaf, menu, { ...leaf, submenu: [] }, SIDEBAR_MENU_ACCESS);
		}
	}
	return result;
}

/**
 * Nodi di partenza dall'albero globale corrente, struttura e ordine identici al menu
 * standard: categorie → categorie del profilo (stesso id = String(pg_id)), pagine
 * navigabili con figli (es. Automezzi) → voce con sottomenu. Un profilo appena creato
 * e attivato mostra quindi esattamente il menu standard.
 */
export function seedMenuOrgNodes(menu: SidebarMenu[]): MenuOrgNode[] {
	const seedChildren = (items: SidebarMenu[]): MenuOrgNode[] => {
		const out: MenuOrgNode[] = [];
		for (const item of items || []) {
			if (isMenuCategory(item)) {
				out.push({
					type: 'category',
					cat: { id: String(item.pg_id), label: item.pg_title, icon: item.styleclass, items: seedChildren(item.submenu || []) },
				});
			} else {
				// i figli restano sotto la pagina (prima diventavano fratelli al primo livello:
				// nel portale il menu si "raddoppiava" e i titoli omonimi comparivano due volte)
				const children = seedChildren(item.submenu || []);
				out.push(children.length ? { type: 'item', pg_id: item.pg_id, items: children } : { type: 'item', pg_id: item.pg_id });
			}
		}
		return out;
	};
	return seedChildren(menu || []);
}

/**
 * Parse sicuro della stringa cmp_menu_org (null se assente/corrotta).
 * Retrocompatibile col formato v1 { version: 1, nodes: [...] } (singolo menu attivo).
 */
export function parseMenuOrgStore(raw: string | null | undefined): CompanyMenuOrgStore | null {
	if (!raw) {
		return null;
	}
	try {
		const parsed = JSON.parse(raw);
		if (!parsed) {
			return null;
		}
		if (Array.isArray(parsed.profiles)) {
			const profiles: MenuOrgProfile[] = parsed.profiles.filter((p: any) => p && p.id && Array.isArray(p.nodes));
			const activeId = profiles.some((p) => p.id === parsed.activeId) ? parsed.activeId : null;
			const hubs: string[] = Array.isArray(parsed.hubs) ? parsed.hubs.filter((h: any) => typeof h === 'string' && h) : [];
			return { version: 2, activeId, profiles, hubs };
		}
		if (Array.isArray(parsed.nodes)) {
			// formato v1: un solo menu, sempre attivo
			return { version: 2, activeId: 'default', profiles: [{ id: 'default', name: 'Menu personalizzato', nodes: parsed.nodes }] };
		}
		return null;
	} catch {
		return null;
	}
}

/** Nodi del profilo attivo (null = usare il menu standard). */
export function activeMenuOrgNodes(store: CompanyMenuOrgStore | null): MenuOrgNode[] | null {
	if (!store?.activeId) {
		return null;
	}
	return store.profiles.find((p) => p.id === store.activeId)?.nodes ?? null;
}

// ---- export / import file (portabile tra portali/ambienti) ----
// Nel file le voci sono identificate dal NOME pagina (pg_name), non dal pg_id:
// gli id cambiano tra portali, il nome è stabile e funge da chiave.

export type MenuOrgFileNode = { type: 'item'; page: string; label?: string; items?: MenuOrgFileChild[] } | { type: 'category'; cat: { label: string; icon?: string; items: MenuOrgFileChild[] } } | { type: 'shortcut'; key: string; items?: MenuOrgFileChild[] };

/** Figlio di categoria nel file: nome pagina nudo oppure nodo (sottocategoria). */
export type MenuOrgFileChild = string | MenuOrgFileNode;

/** Converte i nodi interni (pg_id) nel formato file (nomi pagina). Voci senza riscontro nel menu → scartate. */
export function nodesToFileFormat(nodes: MenuOrgNode[], menu: SidebarMenu[]): MenuOrgFileNode[] {
	const nameById = new Map<number, string>(flattenMenuLeaves(menu).map((l) => [l.pg_id, l.name]));
	const convertChildren = (children: MenuOrgChild[]): MenuOrgFileChild[] => {
		const out: MenuOrgFileChild[] = [];
		for (const child of children || []) {
			const node = asMenuOrgNode(child);
			if (!node) {
				continue;
			}
			if (node.type === 'item') {
				const name = nameById.get(node.pg_id);
				if (name) {
					// nome nudo per le voci semplici; nodo completo se la pagina ha un sottomenu o un nome suo
					out.push(node.items?.length || node.label ? { type: 'item', page: name, ...(node.label ? { label: node.label } : {}), ...(node.items?.length ? { items: convertChildren(node.items) } : {}) } : name);
				}
			} else if (node.type === 'shortcut') {
				// la chiave di catalogo è già portabile
				out.push(node.items?.length ? { type: 'shortcut', key: node.key, items: convertChildren(node.items) } : { type: 'shortcut', key: node.key });
			} else {
				out.push({ type: 'category', cat: { label: node.cat.label, icon: node.cat.icon, items: convertChildren(node.cat.items || []) } });
			}
		}
		return out;
	};
	const out: MenuOrgFileNode[] = [];
	for (const node of nodes || []) {
		if (node.type === 'item') {
			const name = nameById.get(node.pg_id);
			if (name) {
				const fileNode: MenuOrgFileNode = { type: 'item', page: name };
				if (node.label) {
					fileNode.label = node.label;
				}
				if (node.items?.length) {
					fileNode.items = convertChildren(node.items);
				}
				out.push(fileNode);
			}
		} else if (node.type === 'shortcut') {
			out.push(node.items?.length ? { type: 'shortcut', key: node.key, items: convertChildren(node.items) } : { type: 'shortcut', key: node.key });
		} else if (node.cat) {
			out.push({ type: 'category', cat: { label: node.cat.label, icon: node.cat.icon, items: convertChildren(node.cat.items || []) } });
		}
	}
	return out;
}

/**
 * Converte i nodi del file (nomi pagina) in nodi interni (pg_id) rispetto al menu
 * dell'azienda. Nomi non presenti nel menu → scartati (le voci aziendali assenti nel
 * file verranno comunque appese dal normale flusso di normalizzazione). Gli id delle
 * categorie sono rigenerati (quelli del file non sono portabili).
 */
export function fileFormatToNodes(fileNodes: MenuOrgFileNode[], menu: SidebarMenu[]): MenuOrgNode[] {
	// Coda di pg_id per nome (in ordine di menu): pagine OMONIME (es. "Sopralluoghi"
	// cantieri e RFI) si risolvono consumando le occorrenze del file in sequenza,
	// così un nome ripetuto nel file piazza entrambe le pagine invece di una sola.
	const idsByName = new Map<string, number[]>();
	for (const leaf of flattenMenuLeaves(menu)) {
		const queue = idsByName.get(leaf.name) || [];
		queue.push(leaf.pg_id);
		idsByName.set(leaf.name, queue);
	}
	const takeByName = (name: string): number | undefined => {
		const queue = idsByName.get(name);
		return queue && queue.length ? queue.shift() : undefined;
	};
	const parseNode = (node: any): MenuOrgNode | null => {
		if (!node || !node.type) {
			return null;
		}
		if (node.type === 'item') {
			const pg_id = takeByName(String(node.page));
			if (pg_id === undefined) {
				return null;
			}
			const items = Array.isArray(node.items) && node.items.length ? parseChildren(node.items) : undefined;
			const label = typeof node.label === 'string' && node.label.trim() ? node.label.trim() : undefined;
			return { type: 'item', pg_id, ...(label ? { label } : {}), ...(items && items.length ? { items } : {}) };
		}
		if (node.type === 'shortcut' && typeof node.key === 'string' && node.key) {
			// chiavi sconosciute al catalogo vengono comunque conservate: non rendono
			// nulla nel menu (takeShortcut non le risolve) e l'editor le scarta
			const items = Array.isArray(node.items) && node.items.length ? parseChildren(node.items) : undefined;
			return items && items.length ? { type: 'shortcut', key: node.key, items } : { type: 'shortcut', key: node.key };
		}
		if (node.type === 'category' && node.cat) {
			return { type: 'category', cat: { id: newMenuOrgId('c'), label: String(node.cat.label || ''), icon: node.cat.icon ? String(node.cat.icon) : undefined, items: parseChildren(node.cat.items) } };
		}
		return null;
	};
	const parseChildren = (items: any): MenuOrgChild[] => {
		const out: MenuOrgChild[] = [];
		for (const it of Array.isArray(items) ? items : []) {
			if (typeof it === 'string') {
				const pg_id = takeByName(it);
				if (pg_id !== undefined) {
					out.push(pg_id);
				}
			} else {
				const node = parseNode(it);
				if (node) {
					out.push(node);
				}
			}
		}
		return out;
	};
	const out: MenuOrgNode[] = [];
	for (const node of fileNodes || []) {
		const parsed = parseNode(node);
		if (parsed) {
			out.push(parsed);
		}
	}
	return out;
}
